import { Editor } from "@tiptap/react";
import { icons } from "lucide-react";

import { Icon } from "utils/ui";

type BlockCard = {
  name: string;
  label: string;
  description: string;
  iconName: keyof typeof icons;
  action: (editor: Editor) => void;
  isHidden?: (editor: Editor) => boolean;
};

// Curated block list (the "node" fields live in their own chips section, so
// they are intentionally not here). Headings/lists collapse into one "Text".
const BLOCKS: BlockCard[] = [
  {
    name: "text",
    label: "Text",
    description: "Paragraph or heading",
    iconName: "Type",
    action: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    name: "image",
    label: "Image",
    description: "Upload a picture",
    iconName: "Image",
    action: (editor) => editor.chain().focus().setImageBlock({ src: "" }).run(),
  },
  {
    name: "divider",
    label: "Divider",
    description: "Horizontal rule",
    iconName: "Minus",
    isHidden: (editor) => editor.isActive("horizontal-line"),
    action: (editor) => editor.chain().focus().setHorizontalLine().run(),
  },
  {
    name: "spacer",
    label: "Spacer",
    description: "Adjustable vertical gap",
    iconName: "MoveVertical",
    action: (editor) => editor.chain().focus().setSpacerBlock().run(),
  },
  {
    name: "table",
    label: "Table",
    description: "Rows and columns of cells",
    iconName: "Table",
    isHidden: (editor) => editor.isActive("table"),
    action: (editor) => editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run(),
  },
  {
    name: "columns",
    label: "Columns",
    description: "Two-column layout",
    iconName: "Columns2",
    isHidden: (editor) => editor.isActive("columns"),
    action: (editor) =>
      editor
        .chain()
        .focus()
        .setColumns()
        .focus(editor.state.selection.head - 1)
        .run(),
  },
  {
    name: "signature",
    label: "Signature",
    description: "Label + sign-off line",
    iconName: "Signature",
    isHidden: (editor) => editor.isActive("signature"),
    action: (editor) => editor.chain().focus().setSignatureBlock().run(),
  },
  {
    name: "pageBreak",
    label: "Page break",
    description: "Start a new page",
    iconName: "SeparatorHorizontal",
    isHidden: (editor) => editor.isActive("pageBreak"),
    action: (editor) => editor.chain().focus().setPageBreakBlock().run(),
  },
];

/**
 * Block palette shown in the panel's Insert mode. Clicking a card inserts that
 * block at the cursor. Field merge tokens are nodes, not blocks, so they live in
 * the separate Fields chips section.
 */
export const InsertPaletteMenu = ({ editor }: { editor: Editor }) => {
  const blocks = BLOCKS.filter((b) => !b.isHidden || !b.isHidden(editor));

  return (
    <div className="mb-4">
      <div className="flex flex-col gap-2">
        {blocks.map((block) => (
          <button
            key={block.name}
            type="button"
            className="flex items-center gap-3 w-full text-left py-2 px-2.5 border border-[var(--surface-border)] rounded-[10px] bg-[var(--surface-a)] cursor-pointer hover:border-[var(--primary-200)] hover:bg-[var(--surface-b)]"
            title={block.description}
            onClick={() => block.action(editor)}
          >
            <span className="flex-none w-8 h-8 flex items-center justify-center rounded-lg bg-[var(--surface-b)] border border-[var(--surface-border)] text-[var(--text-color-secondary)]">
              <Icon name={block.iconName} size={16} strokeWidth={1.9} />
            </span>
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-[var(--text-color)]">{block.label}</span>
              <span className="text-xs text-[var(--text-color-secondary)]">{block.description}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
